import { useState, useRef, useCallback, useEffect } from 'react'
import Color from '@assets/images/Color.png'
import ColorPickerHover from '@assets/images/ColorPickerHover.png'
import { styled } from 'baseui'
import ColorPickerPopover from './ColorPickerPopover'

export type ColorPickerPopoverPlacement =
  | 'right'
  | 'left'
  | 'top'
  | 'bottom'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'

const WrapPickerTrigger = styled('div', (props: { $isSecondaryStyle?: boolean }) => ({
  width: props.$isSecondaryStyle ? '32px' : '40px',
  height: props.$isSecondaryStyle ? '32px' : '40px',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxSizing: 'border-box',
  borderWidth: '0',
  transition: 'all 0.25s ease-in-out',
  ':hover': {
    cursor: 'pointer',
  },
}))

export interface ColorPickerButtonProps {
  colorSelected: string
  onChange: (color: string, isFinal: boolean) => void
  isSecondaryStyle?: boolean
  popoverPlacement?: ColorPickerPopoverPlacement
  popoverOffset?: { x?: number; y?: number }
}

function ColorPickerButton({
  colorSelected,
  onChange,
  isSecondaryStyle,
  popoverPlacement = 'right',
  popoverOffset,
}: ColorPickerButtonProps) {
  const [isOpenPopover, setIsOpenPopover] = useState(false)
  const buttonRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [popoverPosition, setPopoverPosition] = useState<null | { top: number; left: number }>(null)

  const calculatePopoverPosition = useCallback(
    (rect: DOMRect): { top: number; left: number } => {
      const offsetX = popoverOffset?.x || 0
      const offsetY = popoverOffset?.y || 0
      const popoverWidth = 292
      const popoverHeight = 322
      let top = 0
      let left = 0

      switch (popoverPlacement) {
        case 'right':
          top = rect.top + offsetY
          left = rect.right + offsetX
          break
        case 'left':
          top = rect.top + offsetY
          left = rect.left - popoverWidth - offsetX
          break
        case 'top':
          top = rect.top - popoverHeight - offsetY
          left = rect.left + (rect.width - popoverWidth) / 2 + offsetX
          break
        case 'bottom':
          top = rect.bottom + offsetY
          left = rect.left + (rect.width - popoverWidth) / 2 + offsetX
          break
        case 'top-left':
          top = rect.top - popoverHeight - offsetY
          left = rect.left + offsetX
          break
        case 'top-right':
          top = rect.top - popoverHeight - offsetY
          left = rect.right - popoverWidth - offsetX
          break
        case 'bottom-left':
          top = rect.bottom + offsetY
          left = rect.left + offsetX
          break
        case 'bottom-right':
          top = rect.bottom + offsetY
          left = rect.right - popoverWidth - offsetX
          break
      }

      return { top, left }
    },
    [popoverPlacement, popoverOffset]
  )

  useEffect(() => {
    if (isOpenPopover && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPopoverPosition(calculatePopoverPosition(rect))
    }
  }, [isOpenPopover, calculatePopoverPosition])

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (!isOpenPopover) return
      const target = event.target as Node
      const isClickInsideButton = buttonRef.current?.contains(target)
      const isClickInsidePopover = popoverRef.current?.contains(target)
      if (!isClickInsideButton && !isClickInsidePopover) {
        setIsOpenPopover(false)
      }
    },
    [isOpenPopover]
  )

  useEffect(() => {
    if (isOpenPopover) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpenPopover, handleClickOutside])

  return (
    <>
      <div ref={buttonRef} style={{ position: 'relative' }}>
        <WrapPickerTrigger
          $style={{
            backgroundImage: isOpenPopover ? `url(${ColorPickerHover})` : `url(${Color})`,
            backgroundSize: isSecondaryStyle ? 'cover' : 'auto',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
          $isSecondaryStyle={isSecondaryStyle}
          onClick={() => setIsOpenPopover(prev => !prev)}
        >
          <div style={{ background: '#fff' }} />
        </WrapPickerTrigger>
      </div>
      <ColorPickerPopover
        ref={popoverRef}
        isOpen={isOpenPopover}
        position={popoverPosition}
        color={colorSelected}
        onColorChange={newColor => onChange(newColor, false)}
        onColorFinalChange={color => onChange(color, true)}
      />
    </>
  )
}

export default ColorPickerButton
